/**
 * What the screen shows while a list is on its way.
 *
 * One place for every wait in the app. A line of text ("Učitavanje…") tells the reader
 * that something is happening but not what is coming; a skeleton holds the shape of the
 * table that is about to appear, so the page does not jump when it does.
 *
 * Three shapes, because three things are waited on: a table, a set of cards, and a small
 * area inside a control. Anything slower than a blink gets one of them.
 */
import type { CSSProperties } from "react";

/** One grey block that breathes. Width may be a percentage, so rows do not look stamped. */
function Block({ width, height = 12 }: { width: string; height?: number }) {
  return <span className="sk-block" style={{ width, height }} aria-hidden="true" />;
}

/**
 * A table that has not arrived yet: header line plus `rows` rows of `cols` cells.
 * Widths vary per column so the block reads as text, not as a barcode.
 */
export function TableSkeleton({
  rows = 8,
  cols = 5,
  label,
}: {
  rows?: number;
  cols?: number;
  label?: string;
}) {
  const sirine = ["42%", "68%", "55%", "38%", "60%", "48%", "72%"];
  return (
    <div
      className="sk-table list-card"
      style={{ ["--sk-cols" as string]: String(cols) } as CSSProperties}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      {label && <span className="sr-only">{label}</span>}
      <div className="sk-row sk-head">
        {Array.from({ length: cols }, (_, c) => (
          <Block key={c} width="60%" height={10} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div className="sk-row" key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <Block key={c} width={sirine[(r + c) % sirine.length]} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Cards that have not arrived yet — the board and the dashboard wait in this shape. */
export function CardsSkeleton({ count = 6, label }: { count?: number; label?: string }) {
  return (
    <div className="sk-cards" role="status" aria-busy="true" aria-live="polite">
      {label && <span className="sr-only">{label}</span>}
      {Array.from({ length: count }, (_, i) => (
        <div className="sk-card" key={i}>
          <Block width="70%" height={14} />
          <Block width="45%" />
          <Block width="90%" />
          <Block width="35%" />
        </div>
      ))}
    </div>
  );
}

/**
 * A turning ring for small waits: inside a button, beside a filter that is re-querying.
 * Takes the text colour of whatever contains it, so it works on any surface.
 */
export function Spinner({ size = 16, style }: { size?: number; style?: CSSProperties }) {
  return (
    <span
      className="sk-spinner"
      style={{ width: size, height: size, ...style }}
      role="status"
      aria-hidden="true"
    />
  );
}

/** A whole panel waiting: the spinner plus a word, centred. For detail screens. */
export function PanelLoading({ text }: { text: string }) {
  return (
    <div className="sk-panel" role="status" aria-busy="true" aria-live="polite">
      <Spinner size={20} />
      <span>{text}</span>
    </div>
  );
}
